import React from 'react';
import FormSection, { FieldRow } from './FormSection';
import TextField from './TextField';
import SelectField from './SelectField';
import RadioGroupConditional from './RadioGroupConditional';
import {
    CIVIL_STATE_OPTIONS,
    EDUCATION_LEVEL_OPTIONS,
    GENDER_OPTIONS,
    UF_OPTIONS
} from '../constants/options';
import { maskCep, maskPhone, maskRg, onlyDigits } from '../utils/masks';

/**
 * Sections 1 to 5 of the candidate form (shared by registration and profile edit).
 * `onChange(name, value)` receives flat names; address fields use the "address.<field>" form.
 * `accessChildren` are extra FieldRows appended to the "Contact and Access" section.
 */
const CandidateFormSections = ({ values, errors = {}, onChange, accessChildren }) => {
    const address = values.address || {};

    const handleZipChange = async (name, masked) => {
        onChange(name, masked);
        if (onlyDigits(masked).length !== 8) return;

        try {
            const response = await fetch(`https://viacep.com.br/ws/${onlyDigits(masked)}/json/`);
            const data = await response.json();
            if (!data || data.erro) return;
            if (data.logradouro) onChange('address.street', data.logradouro);
            if (data.bairro) onChange('address.neighborhood', data.bairro);
            if (data.localidade) onChange('address.city', data.localidade);
            if (data.uf) onChange('address.state', data.uf);
        } catch (e) {
            // ZIP lookup is only a convenience; the user can fill the address manually.
        }
    };

    return (
        <>
            <FormSection title="1. Dados Pessoais">
                <FieldRow index={1} label="Nome Completo" required htmlFor="psp-name">
                    <TextField name="name" value={values.name} onChange={onChange} error={errors.name} required placeholder="Digite seu nome completo" autoComplete="name" />
                </FieldRow>
                <FieldRow index={2} label="Data de Nascimento" required htmlFor="psp-birth_date">
                    <TextField name="birth_date" type="date" value={values.birth_date} onChange={onChange} error={errors.birth_date} required autoComplete="bday" />
                </FieldRow>
                <FieldRow index={3} label="Nº Carteira de Identidade" required htmlFor="psp-rg">
                    <TextField
                        name="rg"
                        value={values.rg}
                        onChange={onChange}
                        mask={maskRg}
                        error={errors.rg}
                        required
                        placeholder="Ex: 12.345.678-9"
                        maxLength={12}
                        autoCapitalize="characters"
                    />
                </FieldRow>
                <FieldRow index={4} label="Órgão Expedidor / UF" required htmlFor="psp-rg_emission">
                    <div className="psp-field-row__inline">
                        <TextField name="rg_emission" value={values.rg_emission} onChange={onChange} error={errors.rg_emission} required placeholder="Ex: SSP" />
                        <SelectField name="rg_state" value={values.rg_state} onChange={onChange} error={errors.rg_state} required options={UF_OPTIONS} placeholder="UF" />
                    </div>
                </FieldRow>
                <FieldRow index={5} label="Data de Emissão (RG)" required htmlFor="psp-emission_date_rg">
                    <TextField name="emission_date_rg" type="date" value={values.emission_date_rg} onChange={onChange} error={errors.emission_date_rg} required />
                </FieldRow>
                <FieldRow index={6} label="Nacionalidade" required htmlFor="psp-nationality">
                    <TextField name="nationality" value={values.nationality} onChange={onChange} error={errors.nationality} required />
                </FieldRow>
                <FieldRow index={7} label="Sexo / Gênero" required htmlFor="psp-gender">
                    <SelectField name="gender" value={values.gender} onChange={onChange} error={errors.gender} required options={GENDER_OPTIONS} />
                </FieldRow>
                <FieldRow index={8} label="Estado Civil" required htmlFor="psp-civil_state">
                    <SelectField name="civil_state" value={values.civil_state} onChange={onChange} error={errors.civil_state} required options={CIVIL_STATE_OPTIONS} />
                </FieldRow>
            </FormSection>

            <FormSection title="2. Endereço">
                <FieldRow index={1} label="CEP" required htmlFor="psp-address.zip_code">
                    <TextField name="address.zip_code" value={address.zip_code} onChange={handleZipChange} mask={maskCep} error={errors['address.zip_code']} required placeholder="00000-000" inputMode="numeric" autoComplete="postal-code" />
                </FieldRow>
                <FieldRow index={2} label="Endereço" required htmlFor="psp-address.street">
                    <TextField name="address.street" value={address.street} onChange={onChange} error={errors['address.street']} required autoComplete="address-line1" />
                </FieldRow>
                <FieldRow index={3} label="Número" required htmlFor="psp-address.number">
                    <TextField name="address.number" value={address.number} onChange={onChange} error={errors['address.number']} required />
                </FieldRow>
                <FieldRow index={4} label="Complemento" htmlFor="psp-address.complement">
                    <TextField name="address.complement" value={address.complement} onChange={onChange} error={errors['address.complement']} placeholder="Opcional" />
                </FieldRow>
                <FieldRow index={5} label="Bairro" required htmlFor="psp-address.neighborhood">
                    <TextField name="address.neighborhood" value={address.neighborhood} onChange={onChange} error={errors['address.neighborhood']} required />
                </FieldRow>
                <FieldRow index={6} label="Cidade" required htmlFor="psp-address.city">
                    <TextField name="address.city" value={address.city} onChange={onChange} error={errors['address.city']} required autoComplete="address-level2" />
                </FieldRow>
                <FieldRow index={7} label="Estado" required htmlFor="psp-address.state">
                    <SelectField name="address.state" value={address.state} onChange={onChange} error={errors['address.state']} required options={UF_OPTIONS} placeholder="UF" />
                </FieldRow>
            </FormSection>

            <FormSection title="3. Contato e Acesso">
                <FieldRow index={1} label="Telefone" required htmlFor="psp-phone_contact">
                    <TextField name="phone_contact" type="tel" value={values.phone_contact} onChange={onChange} mask={maskPhone} error={errors.phone_contact} required placeholder="(00) 00000-0000" autoComplete="tel" />
                </FieldRow>
                <FieldRow index={2} label="E-mail" required htmlFor="psp-email">
                    <TextField name="email" type="email" value={values.email} onChange={onChange} error={errors.email} required placeholder="seu@email.com" autoComplete="email" />
                </FieldRow>
                {accessChildren}
            </FormSection>

            <FormSection title="4. Declarações e Condições Especiais">
                <FieldRow index={1} label="Possui nome social? (Decreto nº 8.727/2016)">
                    <RadioGroupConditional name="social_name" label="Nome social" value={values.social_name} onChange={onChange} error={errors.social_name} placeholder="Informe o nome social" />
                </FieldRow>
                <FieldRow index={2} label="Necessita de condições especiais para a prova?">
                    <RadioGroupConditional name="special_conditions" label="Condições especiais" value={values.special_conditions} onChange={onChange} error={errors.special_conditions} placeholder="Descreva as condições necessárias" />
                </FieldRow>
                <FieldRow index={3} label="Deseja concorrer como PCD?">
                    <RadioGroupConditional name="pcd" label="PCD" value={values.pcd} onChange={onChange} error={errors.pcd} placeholder="Informe a deficiência" />
                </FieldRow>
            </FormSection>

            <FormSection title="5. Formação e Experiência">
                <FieldRow index={1} label="Escolaridade" required htmlFor="psp-schooling">
                    <SelectField name="schooling" value={values.schooling} onChange={onChange} error={errors.schooling} required options={EDUCATION_LEVEL_OPTIONS} />
                </FieldRow>
                <FieldRow index={2} label="Cursos" htmlFor="psp-courses">
                    <TextField name="courses" multiline value={values.courses} onChange={onChange} error={errors.courses} placeholder="Cursos e certificações (opcional)" />
                </FieldRow>
                <FieldRow index={3} label="Experiência Prévia" htmlFor="psp-experience">
                    <TextField name="experience" multiline value={values.experience} onChange={onChange} error={errors.experience} placeholder="Descreva sua experiência profissional (opcional)" />
                </FieldRow>
            </FormSection>
        </>
    );
};

export default CandidateFormSections;
